import React, { useState, useEffect, useCallback, useMemo } from 'react';
import WorkflowModal from './WorkflowModal';
import { useWorkflowView, WORKFLOW_VIEW_CHANGED_EVENT } from '../contexts/WorkflowViewContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkflowFavorites } from '../hooks/useWorkflowFavorites';
import { useRecentWorkflows } from '../hooks/useRecentWorkflows';
import { WorkflowCategoryDetail, WorkflowTool } from '../types/workflow.types';
import { pickIcon } from '../utils/categoryIcon';
import { SUITES, getSuiteForType } from '../types/suites';

// Tool configuration by card type (for client type filtering)
const TOOL_CONFIG = {
  customer: [
    'cyber_auto',
    'cyber_benchmark',
    'cyber_renewal_delta',
    'cyber_war_systemic',
    'cyber_ransomware_trap',
    'cyber_quote_comparison',
    'do_auto',
    'do_benchmark',
    'do_renewal_delta',
    'do_distress_scanner',
    'do_emerging_exposure',
    'home_auto',
    'home_owner_benchmark',
    'home_tenant_condo_benchmark',
    'home_quote_comparison',
    'home_renewal_delta',
    'home_dp_rental_benchmark',
    'home_dp_rental_renewal_delta',
    'home_hnw_benchmark',
    'plines_auto',
    'pa_benchmark',
    'pa_quote_comparison',
    'pa_renewal_delta',
    'umb_benchmark',
    'umb_quote_comparison',
    'umb_renewal_delta',
    'rt_auto',
    'rt_requirements_reviewer',
    'rt_conformance_alignment',
    'premdev_auto',
    'premdev_workers_comp',
    'premdev_prof_liability',
    'premdev_general_liability',
    'premdev_property',
    'premdev_package_bop',
    'lossrun_auto',
    'lossrun_fact_engine',
    'lossrun_mitigation_wc',
    're_auto',
    're_policy_extraction',
    're_risk_transfer',
    're_br_financials',
    're_zone_extractor',
    're_portfolio_brief',
    'rt_compliance_verifier',
    'cl_auto',
    'cl_gl_benchmark',
    'cl_property_benchmark',
    'cl_commercial_auto_benchmark',
    'cl_inland_marine_benchmark',
    'cl_excess_benchmark',
    'cl_package_benchmark',
    'cl_bop_benchmark',
    'cl_nytpao_flow',
    'cl_package_quote_comparison',
    'cl_bop_quote_comparison',
    'cl_xarch_quote_comparison',
    'cl_package_renewal_delta',
    'cl_bop_renewal_delta',
    'ca_auto',
    'ca_coverage_determination',
    'ca_portfolio_determination',
    'ca_carrier_position_audit',
    'ntr_auto',
    'ntr_first_party',
    'ntr_property_liability',
    'ntr_mgmt_professional',
    'document_comparison',
    'renewal',
    'year_over_year',
    'yoy_renewal',
    'personal_lines_yoy_renewal',
    'commercial_lines_exec_renewal',
    'client_renewal_presentation',
    'loss_run_analyzer_v4', // "Loss Run Analyzer NEW" — the consolidated "Loss Run Analysis" entry
    'insurance_requirements',
    'financial_analysis',
    'research_tool',
    'certificate_of_effort',
    'coverage_analysis',
    'rate_analysis',
    'portfolio_analyzer',
    'homeowners_comparison',
    'personal_umbrella'
  ],
  prospect: [
    'cyber_auto',
    'cyber_benchmark',
    'cyber_renewal_delta',
    'cyber_war_systemic',
    'cyber_ransomware_trap',
    'cyber_quote_comparison',
    'do_auto',
    'do_benchmark',
    'do_renewal_delta',
    'do_distress_scanner',
    'do_emerging_exposure',
    'home_auto',
    'home_owner_benchmark',
    'home_tenant_condo_benchmark',
    'home_quote_comparison',
    'home_renewal_delta',
    'home_dp_rental_benchmark',
    'home_dp_rental_renewal_delta',
    'home_hnw_benchmark',
    'plines_auto',
    'pa_benchmark',
    'pa_quote_comparison',
    'pa_renewal_delta',
    'umb_benchmark',
    'umb_quote_comparison',
    'umb_renewal_delta',
    'rt_auto',
    'rt_requirements_reviewer',
    'rt_conformance_alignment',
    'premdev_auto',
    'premdev_workers_comp',
    'premdev_prof_liability',
    'premdev_general_liability',
    'premdev_property',
    'premdev_package_bop',
    'lossrun_auto',
    'lossrun_fact_engine',
    'lossrun_mitigation_wc',
    're_auto',
    're_policy_extraction',
    're_risk_transfer',
    're_br_financials',
    're_zone_extractor',
    're_portfolio_brief',
    'rt_compliance_verifier',
    'cl_auto',
    'cl_gl_benchmark',
    'cl_property_benchmark',
    'cl_commercial_auto_benchmark',
    'cl_inland_marine_benchmark',
    'cl_excess_benchmark',
    'cl_package_benchmark',
    'cl_bop_benchmark',
    'cl_nytpao_flow',
    'cl_package_quote_comparison',
    'cl_bop_quote_comparison',
    'cl_xarch_quote_comparison',
    'cl_package_renewal_delta',
    'cl_bop_renewal_delta',
    'ca_auto',
    'ca_coverage_determination',
    'ca_portfolio_determination',
    'ca_carrier_position_audit',
    'ntr_auto',
    'ntr_first_party',
    'ntr_property_liability',
    'ntr_mgmt_professional',
    'document_comparison',
    'year_over_year',
    'yoy_renewal',
    'personal_lines_yoy_renewal',
    'commercial_lines_exec_renewal',
    'client_renewal_presentation',
    'loss_run_analyzer_v4', // "Loss Run Analyzer NEW" — the consolidated "Loss Run Analysis" entry
    'insurance_requirements',
    'financial_analysis',
    'research_tool',
    'certificate_of_effort',
    'coverage_analysis',
    'rate_analysis',
    'portfolio_analyzer',
    'homeowners_comparison',
    'personal_umbrella'
  ],
  suspect: [
    'research_tool',
    'loss_run_analyzer_v4', // "Loss Run Analyzer NEW" — the consolidated "Loss Run Analysis" entry
    'year_over_year',
    'yoy_renewal',
    'personal_lines_yoy_renewal',
    'commercial_lines_exec_renewal',
    'client_renewal_presentation',
    'insurance_requirements',
    'financial_analysis',
    'certificate_of_effort',
    'rate_analysis',
    'portfolio_analyzer',
    'homeowners_comparison',
    'personal_umbrella'
  ]
};

// Tool display configuration
const TOOL_INFO: Record<string, { name: string; icon: string; description: string; route: string }> = {
  cyber_auto: {
    name: 'Cyber (Automatic)',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7A2,2 0 0,1 14,9C14,9.74 13.6,10.39 13,10.73V13H14V15H10V13H11V10.73C10.4,10.39 10,9.74 10,9A2,2 0 0,1 12,7Z',
    description: 'Drop in cyber policy docs — RiskNinja picks the right analysis automatically.',
    route: '/cyber-auto'
  },
  cyber_benchmark: {
    name: 'Cyber Policy Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Benchmarks a cyber policy component-by-component against a best-available standard — coverage matrix, detailed report, and executive summary, including the ransomware coverage-trap check.',
    route: '/cyber-benchmark'
  },
  cyber_renewal_delta: {
    name: 'Cyber Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Diffs a cyber policy against its renewal to catch silent coverage degradation — sublimit drops, definition narrowing, and new conditions the grade alone would miss.',
    route: '/cyber-renewal-delta'
  },
  cyber_war_systemic: {
    name: 'Cyber War & Systemic Exclusion Analyzer',
    icon: 'M12,2L4,5V11.09C4,16.14 7.41,20.85 12,22C16.59,20.85 20,16.14 20,11.09V5L12,2M12,4.14L18,6.68V11.09C18,15.09 15.45,18.79 12,19.92C8.55,18.79 6,15.1 6,11.09V6.68L12,4.14M11,7V13H13V7H11M11,15V17H13V15H11Z',
    description: 'Fast, dated read on whether the war/hostile-act/cyber-operations and systemic-event wording is narrow enough to hold — flags wording that swallows ordinary ransomware.',
    route: '/cyber-war-systemic'
  },
  cyber_ransomware_trap: {
    name: 'Cyber Ransomware Trap Scanner',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M11,7H13V9H11V7M11,11H13V17H11V11Z',
    description: 'Triage check: does the ransomware coverage actually work for this insured, or is it gated by security-control conditions they don’t meet?',
    route: '/cyber-ransomware-trap'
  },
  cyber_quote_comparison: {
    name: 'Cyber Quote Comparison',
    icon: 'M9,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H9V3M11,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3H11V21M13,7H19V9H13V7M13,11H19V13H13V11M13,15H17V17H13V15Z',
    description: 'Compares two or more competing cyber quotes side by side against the cyber benchmark — coverage differentials, subjectivities you may not be able to meet, and traps. Never ranks the quotes.',
    route: '/cyber-quote-comparison'
  },
  do_auto: {
    name: 'D&O (Automatic)',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7A2,2 0 0,1 14,9C14,9.74 13.6,10.39 13,10.73V13H14V15H10V13H11V10.73C10.4,10.39 10,9.74 10,9A2,2 0 0,1 12,7Z',
    description: 'Drop in D&O program docs — RiskNinja picks the right analysis automatically.',
    route: '/do-auto'
  },
  do_benchmark: {
    name: 'D&O Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a D&O program (or compares competing quotes) component-by-component against a frame-switched benchmark — ranked concerns, never verdicts.',
    route: '/do-benchmark'
  },
  do_renewal_delta: {
    name: 'D&O Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Diffs a prior D&O program against its renewal to catch silent coverage degradation — sublimit and tower movements, definition narrowing, new conditions.',
    route: '/do-renewal-delta'
  },
  do_distress_scanner: {
    name: 'D&O Financial Distress Scanner',
    icon: 'M11,7H13V9H11V7M11,11H13V17H11V11M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1Z',
    description: 'Fast triage: does this D&O program’s structure survive the insolvency moment, given the insured’s actual financial posture?',
    route: '/do-distress-scanner'
  },
  do_emerging_exposure: {
    name: 'D&O Emerging-Exposure Analyzer',
    icon: 'M12,2L4,5V11.09C4,16.14 7.41,20.85 12,22C16.59,20.85 20,16.14 20,11.09V5L12,2M12,4.14L18,6.68V11.09C18,15.09 15.45,18.79 12,19.92C8.55,18.79 6,15.1 6,11.09V6.68L12,4.14M11,7V13H13V7H11M11,15V17H13V15H11Z',
    description: 'Classifies how a D&O form treats AI, cyber, and crypto exclusion wording — connector breadth, carve-back architecture, and whether it reaches governance claims.',
    route: '/do-emerging-exposure'
  },
  home_auto: {
    name: 'Home Insurance (Automatic)',
    icon: 'M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z',
    description: 'Drop in home insurance docs — RiskNinja picks the right analysis automatically (owner, tenant/condo, quotes, renewal, or rental).',
    route: '/home-auto'
  },
  home_owner_benchmark: {
    name: 'HO Owner Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades an owner-occupied (HO-3/HO-5) homeowners program against a best-available standard — client/broker report over a consultant technical annex.',
    route: '/home-owner-benchmark'
  },
  home_tenant_condo_benchmark: {
    name: 'HO Tenant + Condo Benchmark',
    icon: 'M12,3L2,12H5V20H19V12H22L12,3M12,8A2,2 0 0,1 14,10A2,2 0 0,1 12,12A2,2 0 0,1 10,10A2,2 0 0,1 12,8Z',
    description: 'Grades HO-4 (tenant) or HO-6 (condo/co-op) paper, including the unit/master seam — loss assessment vs. master deductible, responsibility zone.',
    route: '/home-tenant-condo-benchmark'
  },
  home_quote_comparison: {
    name: 'HO Quote Comparison',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'N-way comparison of 2+ competing home insurance quotes for one dwelling — ranked coverage differentials, quotes never ranked.',
    route: '/home-quote-comparison'
  },
  home_renewal_delta: {
    name: 'HO Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Decomposes what moved a homeowners renewal premium — basis, rate, classification, structure, credits — and flags silent coverage trades.',
    route: '/home-renewal-delta'
  },
  home_dp_rental_benchmark: {
    name: 'Dwelling Fire / Rental Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a rental-dwelling (DP-1/2/3) or landlord program, including the primary premises-liability chain check.',
    route: '/home-dp-rental-benchmark'
  },
  home_dp_rental_renewal_delta: {
    name: 'Dwelling Fire / Rental Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Decomposes what moved a rental-dwelling renewal premium — basis, rate, classification, structure, credits — and flags silent coverage trades.',
    route: '/home-dp-rental-renewal-delta'
  },
  home_hnw_benchmark: {
    name: 'HNW Homeowners Benchmark',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7A2,2 0 0,1 14,9C14,9.74 13.6,10.39 13,10.73V13H14V15H10V13H11V10.73C10.4,10.39 10,9.74 10,9A2,2 0 0,1 12,7Z',
    description: 'Grades non-ISO high-net-worth carrier paper (Chubb Masterpiece, PURE, AIG Private Client, Cincinnati Executive Capstone and kin) against a floor-read benchmark.',
    route: '/home-hnw-benchmark'
  },
  plines_auto: {
    name: 'Personal Lines (Automatic)',
    icon: 'M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z',
    description: 'Drop in personal auto or umbrella docs — RiskNinja picks the right analysis automatically.',
    route: '/plines-auto'
  },
  pa_benchmark: {
    name: 'Personal Auto Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a single-program personal auto policy component-by-component against a best-available standard, with a reconciled Statement of Vehicles & Drivers.',
    route: '/pa-benchmark'
  },
  pa_quote_comparison: {
    name: 'Personal Auto Quote Comparison',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'N-way comparison of 2+ competing personal auto quotes or programs — ranked coverage differentials, quotes never ranked.',
    route: '/pa-quote-comparison'
  },
  pa_renewal_delta: {
    name: 'Personal Auto Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Diffs a same-insurer prior/renewal auto pair per vehicle and operator — coverage movement plus premium movement carried as reported fact.',
    route: '/pa-renewal-delta'
  },
  umb_benchmark: {
    name: 'Personal Umbrella Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a personal umbrella/excess tower against underlying-attachment facts — no "buy more" language, ranked concerns only.',
    route: '/umb-benchmark'
  },
  umb_quote_comparison: {
    name: 'Personal Umbrella Quote Comparison',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'N-way comparison of 2+ competing personal umbrella towers — ranked coverage differentials, towers never ranked.',
    route: '/umb-quote-comparison'
  },
  umb_renewal_delta: {
    name: 'Personal Umbrella Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Diffs a same-insurer prior/renewal umbrella pair — a different-insurer pair routes to the quote comparison tool instead (replacement, not renewal).',
    route: '/umb-renewal-delta'
  },
  rt_auto: {
    name: 'Requirements Consulting (Automatic)',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Drop in a requirements clause and/or a program — RiskNinja picks the right consulting instrument automatically.',
    route: '/rt-auto'
  },
  rt_requirements_reviewer: {
    name: 'Insurance Requirements Reviewer (RT-1R)',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M13,9V3.5L18.5,9M8,12V14H16V12H8M8,16V18H13V16H8Z',
    description: 'Audits a requirements clause the client imposes or was handed — client-side inputs only, no delivered program ingested.',
    route: '/rt-requirements-reviewer'
  },
  rt_conformance_alignment: {
    name: 'Requirements Compliance Alignment (RT-3)',
    icon: 'M9,16.17L4.83,12L3.41,13.41L9,19L21,7L19.59,5.59L9,16.17Z',
    description: 'Aligns the client\'s own program against externally-imposed requirements — C/D/S/X per-requirement triage, corrective-first redlines, Residual Schedule for real shortfalls.',
    route: '/rt-conformance-alignment'
  },
  premdev_auto: {
    name: 'Premium Development (Automatic)',
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Drop in premium/rating docs — RiskNinja detects the line of business and decomposes what moved the premium.',
    route: '/premdev-auto'
  },
  premdev_workers_comp: {
    name: 'Premium Dev — Workers Comp',
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Decomposes workers comp premium — payroll, classification, experience mod, schedule credits, discounts, and dividend plans.',
    route: '/premdev-workers-comp'
  },
  premdev_prof_liability: {
    name: 'Premium Dev — Professional Liab + D&O',
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Decomposes E&O/D&O/EPL/Fiduciary premium — claims-made maturity, tower/ILF relativity, shared-limit erosion.',
    route: '/premdev-prof-liability'
  },
  premdev_general_liability: {
    name: 'Premium Dev — General Liability',
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Decomposes GL premium — exposure, classification, rate, limit/attachment, modifications, and umbrella/excess layers.',
    route: '/premdev-general-liability'
  },
  premdev_property: {
    name: 'Premium Dev — Property',
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Decomposes commercial property premium — TIV/rate movement, TRIA dual presentation, blanket/scheduled and tower structure.',
    route: '/premdev-property'
  },
  premdev_package_bop: {
    name: 'Premium Dev — Package / BOP',
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Decomposes package/BOP premium at two tiers — per coverage part and at the package top (composition, credit, minimums, terrorism).',
    route: '/premdev-package-bop'
  },
  lossrun_auto: {
    name: 'Loss Runs (Automatic)',
    icon: 'M9,2V8H11V11H8V13H11V16H8V22H16V16H13V13H16V11H13V8H16V2H9M4,8V10H7V8H4M17,8V10H20V8H17M4,13V15H7V13H4M17,13V15H20V13H17Z',
    description: 'Drop in loss runs — extracts claim-level facts (CLM-1), or run standalone against a past extraction to build a mitigation / underwriter deliverable.',
    route: '/lossrun-auto'
  },
  lossrun_fact_engine: {
    name: 'Loss History Fact Engine (CLM-1)',
    icon: 'M9,2V8H11V11H8V13H11V16H8V22H16V16H13V13H16V11H13V8H16V2H9M4,8V10H7V8H4M17,8V10H20V8H17M4,13V15H7V13H4M17,13V15H20V13H17Z',
    description: 'Forensic loss run extraction with a claim-number census gate, cross-foot validation, and multi-year frequency/severity/cause analysis. Facts only — no recommendations.',
    route: '/lossrun-fact-engine'
  },
  lossrun_mitigation_wc: {
    name: 'Loss Mitigation / UW Presentation — WC',
    icon: 'M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16',
    description: 'Consumes a CLM-1 extraction and produces either a client-facing Stewardship report or a carrier-facing Underwriter Presentation — matched controls, tiered recommendations.',
    route: '/lossrun-mitigation-wc'
  },
  re_auto: {
    name: 'Real Estate (Automatic)',
    icon: 'M12,3L2,12H5V20H19V12H22L12,3M12,8A2,2 0 0,1 14,10A2,2 0 0,1 12,12A2,2 0 0,1 10,10A2,2 0 0,1 12,8M6,14H18V18H6V14Z',
    description: 'Drop in real estate docs — RiskNinja picks the right extractor automatically (policy/SOV, contract, BR financials, or procurement).',
    route: '/re-auto'
  },
  re_policy_extraction: {
    name: 'RE Portfolio Coverage Extractor',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Extracts cited, reconciled exhibits from real estate portfolios — property SOV, liability SOV, forms schedule, or construction-pipeline tracker.',
    route: '/re-policy-extraction'
  },
  re_risk_transfer: {
    name: 'RE Contract Risk Transfer Analyzer',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Extracts a contract\'s risk-transfer architecture — indemnification, insurance requirements, AI status, subrogation control — and audits it against responding policies where supplied.',
    route: '/re-risk-transfer'
  },
  re_br_financials: {
    name: "Builder's Risk Financials Extractor",
    icon: 'M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z',
    description: 'Categorizes builder\'s risk financials from a construction budget and income proforma — soft-cost worksheet for forecasting DSU/soft-cost limits.',
    route: '/re-br-financials'
  },
  re_zone_extractor: {
    name: 'Procurement Zone-Value Extractor',
    icon: 'M20,8H17.19C16.74,7.22 16.12,6.55 15.37,6.04L17,4.41L15.59,3L13.42,5.17C12.96,5.06 12.5,5 12,5C11.5,5 11.04,5.06 10.59,5.17L8.41,3L7,4.41L8.62,6.04C7.88,6.55 7.26,7.22 6.81,8H4V10H6.09C6.03,10.33 6,10.66 6,11V12H4V14H6V15C6,15.34 6.03,15.67 6.09,16H4V18H6.81C8.47,20.87 12.14,21.84 15,20.18C15.91,19.66 16.67,18.9 17.19,18H20V16H17.91C17.97,15.67 18,15.34 18,15V14H20V12H18V11C18,10.66 17.97,10.33 17.91,10H20V8Z',
    description: 'Extracts transit and off-site storage exposure values from procurement plans and purchase orders — feeds the builder\'s risk sublimit check.',
    route: '/re-zone-extractor'
  },
  re_portfolio_brief: {
    name: 'RE Portfolio Board Brief',
    icon: 'M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V9H7V7M7,11H17V13H7V11M7,15H14V17H7V15Z',
    description: 'Compiles already-extracted RE producer reports into one reconciled portfolio brief — surfaces the cross-block truths (SOV vs. tracker disagreements, procurement exposure vs. transit sublimit, dropped buildings).',
    route: '/re-portfolio-brief'
  },
  document_comparison: {
    name: 'Document Comparison',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'Compare like documents side by side — applications, financial statements, and similar paperwork. To compare quotes or policies across terms, use a Renewal workflow.',
    route: '/comparison'
  },
  renewal: {
    name: 'Renewal',
    icon: 'M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z M18.78,7.22L19.07,7.93L20,8.07L19.22,8.78L19.43,9.71L18.78,9.22L18.07,9.71L18.22,8.78L17.43,8.07L18.36,7.93L18.78,7.22M12.5,8V12.25L16,14.33L15.28,15.54L11,13V8H12.5Z',
    description: 'Process renewal applications and compare with previous year',
    route: '/renewal'
  },
  year_over_year: {
    name: 'Year-over-Year Comparison',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Broker analysis: compare a single policy across two terms to spot changes and trends',
    route: '/year-over-year'
  },
  yoy_renewal: {
    name: 'Multi-Line Renewal Review',
    icon: 'M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V5H19V19H5V5H7V7M12,17V15H17V17H12M7,17V15H9V17H7M7,13V11H9V13H7M10,13V11H17V13H10Z',
    description: 'Broker analysis: comprehensive multi-line insurance program renewal with strategic recommendations',
    route: '/yoy-renewal'
  },
  personal_lines_yoy_renewal: {
    name: 'Personal Lines Renewal Review',
    icon: 'M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z',
    description: 'Broker analysis: homeowners, auto, umbrella, and specialty coverage renewal review',
    route: '/personal-lines-yoy-renewal'
  },
  commercial_lines_exec_renewal: {
    name: 'Executive Renewal Summary',
    icon: 'M18,15H16V17H18M18,11H16V13H18M20,19H12V17H14V15H12V13H14V11H12V9H20M10,7H8V5H10M10,11H8V9H10M10,15H8V13H10M10,19H8V17H10M6,7H4V5H6M6,11H4V9H6M6,15H4V13H6M6,19H4V17H6M12,7V3H2V21H22V7H12Z',
    description: 'Client-facing deliverable: executive summary + risk intelligence for commercial lines renewal',
    route: '/commercial-lines-exec-renewal'
  },
  loss_run_analysis: {
    name: 'Loss Run Analysis (superseded)',
    icon: 'M9,2V8H11V11H8V13H11V16H8V22H16V16H13V13H16V11H13V8H16V2H9M4,8V10H7V8H4M17,8V10H20V8H17M4,13V15H7V13H4M17,13V15H20V13H17Z',
    description: 'Superseded by the consolidated Loss Run Analysis tool — kept only so old reports still deep-link correctly.',
    route: '/loss-run-analysis'
  },
  loss_run_analyzer: {
    name: 'Loss Run Analyzer (superseded)',
    icon: 'M9,2V8H11V11H8V13H11V16H8V22H16V16H13V13H16V11H13V8H16V2H9M4,8V10H7V8H4M17,8V10H20V8H17M4,13V15H7V13H4M17,13V15H20V13H17Z',
    description: 'Superseded by the consolidated Loss Run Analysis tool — kept only so old reports still deep-link correctly.',
    route: '/loss-run-analyzer'
  },
  loss_run_analyzer_v4: {
    name: 'Loss Run Analysis',
    icon: 'M9,2V8H11V11H8V13H11V16H8V22H16V16H13V13H16V11H13V8H16V2H9M4,8V10H7V8H4M17,8V10H20V8H17M4,13V15H7V13H4M17,13V15H20V13H17Z',
    description: 'Forensic loss run analysis with evidence-quotation gates, multi-line-item aggregation, and per-claim state attribution',
    route: '/loss-run-analyzer'
  },
  loss_run_analyzer_v4_3: {
    name: 'Loss Run Analysis (superseded)',
    icon: 'M9,2V8H11V11H8V13H11V16H8V22H16V16H13V13H16V11H13V8H16V2H9M4,8V10H7V8H4M17,8V10H20V8H17M4,13V15H7V13H4M17,13V15H20V13H17Z',
    description: 'Superseded by the consolidated Loss Run Analysis tool — kept only so old reports still deep-link correctly.',
    route: '/loss-run-analyzer'
  },
  research_tool: {
    name: 'Research Tool',
    icon: 'M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z',
    description: 'Research companies, risks, and market information',
    route: '/research'
  },
  certificate_of_effort: {
    name: 'Certificate of Effort',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Generate Certificate of Effort for surplus lines insurance placement',
    route: '/certificate-of-effort'
  },
  coverage_analysis: {
    name: 'Coverage Analysis',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Comprehensive analysis of insurance policy coverage and gaps',
    route: '/coverage-analysis'
  },
  insurance_requirements: {
    name: 'Insurance Requirements',
    icon: 'M9,5H7A2,2 0 0,0 5,7V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V7A2,2 0 0,0 17,5H15V4A2,2 0 0,0 13,2H11A2,2 0 0,0 9,4V5M9,7V4H15V7H9M7,9H17V19H7V9M9,11V13H15V11H9M9,15V17H13V15H9Z',
    description: 'Analyze contractual insurance requirements against policy coverage for compliance',
    route: '/insurance-requirements'
  },
  financial_analysis: {
    name: 'Financial Analysis',
    icon: 'M5,6H23V18H5V6M14,9A3,3 0 0,1 17,12A3,3 0 0,1 14,15A3,3 0 0,1 11,12A3,3 0 0,1 14,9M9,8A2,2 0 0,1 7,10V14A2,2 0 0,1 9,16H19A2,2 0 0,1 21,14V10A2,2 0 0,1 19,8H9M1,10H3V20H19V22H1V10Z',
    description: 'Analyze financial statements for underwriting risk assessment and ERM insights',
    route: '/financial-analysis'
  },
  client_renewal_presentation: {
    name: 'Client Renewal Presentation',
    icon: 'M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V5H19V19H5V5H7V7M12,17V15H17V17H12M7,17V15H9V17H7M7,13V11H9V13H7M10,13V11H17V13H10Z',
    description: 'Client-facing deliverable: renewal presentation with coverage analysis and talking points',
    route: '/client-renewal-presentation'
  },
  rate_analysis: {
    name: 'Rate Analysis',
    icon: 'M3,22V8H7V22H3M10,22V2H14V22H10M17,22V14H21V22H17Z',
    description: 'Premium development analysis - decompose rate, basis, classification, and modification changes across policy periods',
    route: '/rate-analysis'
  },
  portfolio_analyzer: {
    name: 'Portfolio Analyzer',
    icon: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V5H19V19M17,17H7V7H17V17M15,15V9H9V15H15Z',
    description: 'Multi-policy scenario analysis - trace exposures through coverage layers and across policy lines',
    route: '/portfolio-analyzer'
  },
  homeowners_comparison: {
    name: 'Homeowners Insurance Comparison',
    icon: 'M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z',
    description: 'Compare homeowners insurance quotes and policies with comprehensive coverage gap analysis',
    route: '/homeowners-comparison'
  },
  personal_umbrella: {
    name: 'Personal Umbrella Analysis',
    icon: 'M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M12,4C16.42,4 20,7.58 20,12H4C4,7.58 7.58,4 12,4M4,14H20C20,18.42 16.42,22 12,22C7.58,22 4,18.42 4,14Z',
    description: 'Analyze personal umbrella and excess liability policies — exposure mapping, drop-down matrix, compound failure detection',
    route: '/personal-umbrella'
  },
  rt_compliance_verifier: {
    name: 'Compliance Verifier (RT-2)',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Verifies whether a counterparty’s delivered insurance actually meets the requirements you imposed — per-requirement MET / NOT MET / EVIDENCE-INSUFFICIENT, tier-qualified to the evidence delivered.',
    route: '/rt-compliance-verifier'
  },
  cl_auto: {
    name: 'Commercial Lines (Automatic)',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7A2,2 0 0,1 14,9C14,9.74 13.6,10.39 13,10.73V13H14V15H10V13H11V10.73C10.4,10.39 10,9.74 10,9A2,2 0 0,1 12,7Z',
    description: 'Drop in a commercial P&C program — RiskNinja detects the line and instrument architecture and picks the right chair automatically.',
    route: '/cl-auto'
  },
  cl_gl_benchmark: {
    name: 'GL Benchmark (CL-GL)',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a submitted general liability program (including Products/Completed Operations) component-by-component against a frame-switched benchmark — ranked concerns, never verdicts.',
    route: '/cl-gl-benchmark'
  },
  cl_property_benchmark: {
    name: 'Commercial Property Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a submitted commercial property program against a benchmark yardstick, per scheduled location — ranked concerns, never verdicts.',
    route: '/cl-property-benchmark'
  },
  cl_commercial_auto_benchmark: {
    name: 'Commercial Auto Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a submitted commercial auto program against a benchmark yardstick, resolving the covered-auto designation map first — ranked concerns, never verdicts.',
    route: '/cl-commercial-auto-benchmark'
  },
  cl_inland_marine_benchmark: {
    name: 'Inland Marine Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades a submitted commercial inland marine program (contractors equipment, transit, cargo, bailee) against a benchmark yardstick, per item class.',
    route: '/cl-inland-marine-benchmark'
  },
  cl_excess_benchmark: {
    name: 'Excess & Umbrella Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Reads a full commercial liability tower — every excess/umbrella layer plus the underlying policies it attaches over — for where it does and does not reach.',
    route: '/cl-excess-benchmark'
  },
  cl_package_benchmark: {
    name: 'Commercial Package Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Grades the package tier of a commercial package policy — what exists because coverage parts sit under one declarations set — never the individual parts.',
    route: '/cl-package-benchmark'
  },
  cl_bop_benchmark: {
    name: 'BOP Benchmark',
    icon: 'M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z',
    description: 'Benchmarks one pre-integrated businessowners form — what it grants, what has been subtracted from it, and where the grant set stops.',
    route: '/cl-bop-benchmark'
  },
  cl_nytpao_flow: {
    name: 'NY Action-Over Flow Analysis',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Reads a New York construction risk-transfer pathway across contract, GL, workers’-comp, and excess — states each of the 13 chain links open, closed, or silent. Jurisdiction-locked to NY construction.',
    route: '/cl-nytpao-flow'
  },
  cl_package_quote_comparison: {
    name: 'Package Quote Comparison',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'Compares two or more contemporaneous commercial package submissions criterion-by-criterion — named differences, never a winner.',
    route: '/cl-package-quote-comparison'
  },
  cl_bop_quote_comparison: {
    name: 'BOP Quote Comparison',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'Compares two or more contemporaneous businessowners quotes — position (in-form / elected / attached) and reach reported side by side, never a winner.',
    route: '/cl-bop-quote-comparison'
  },
  cl_xarch_quote_comparison: {
    name: 'Cross-Architecture Quote Comparison (PKG↔BOP)',
    icon: 'M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M12,2A2,2 0 0,1 14,4V20A2,2 0 0,1 12,22H2A2,2 0 0,1 0,20V4A2,2 0 0,1 2,2H12M4,6V18H10V6H4M12,6V18H20V6H12Z',
    description: 'Compares one commercial package proposal against one businessowners proposal — each read against its own yardstick, architecture detected from content, never asked.',
    route: '/cl-xarch-quote-comparison'
  },
  cl_package_renewal_delta: {
    name: 'Package Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Reads one commercial package program across two consecutive periods — what moved, in which direction, and what it is made of.',
    route: '/cl-package-renewal-delta'
  },
  cl_bop_renewal_delta: {
    name: 'BOP Renewal Delta (YOY)',
    icon: 'M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z',
    description: 'Reads one businessowners program across two consecutive periods, including the origin ledger — coverage that moved from in-form to attached even where reach did not change.',
    route: '/cl-bop-renewal-delta'
  },
  ca_auto: {
    name: 'Coverage & Claims Analysis (Automatic)',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7A2,2 0 0,1 14,9C14,9.74 13.6,10.39 13,10.73V13H14V15H10V13H11V10.73C10.4,10.39 10,9.74 10,9A2,2 0 0,1 12,7Z',
    description: 'Drop in a policy, program, or carrier letter — RiskNinja routes to the right coverage/claims chair automatically.',
    route: '/ca-auto'
  },
  ca_coverage_determination: {
    name: 'Coverage Determination (CA-1)',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Reads one policy against a stated clause, fact pattern, or claim — a Coverage Determination Record with a three-state map (GRANTED/EXCLUDED/CONTESTED); the verdict is withheld, the scaffolding is not.',
    route: '/ca-coverage-determination'
  },
  ca_portfolio_determination: {
    name: 'Portfolio Coverage Determination (CA-2)',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Reads a program of two or more policies against a stated loss — which policies respond, in what order, and where the structure breaks down.',
    route: '/ca-portfolio-determination'
  },
  ca_carrier_position_audit: {
    name: 'Carrier Position Audit (CA-3)',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Audits a carrier’s denial, reservation of rights, or position letter — what it asserts, what it cites, and what it leaves unaddressed. Never a verdict on whether the carrier is right.',
    route: '/ca-carrier-position-audit'
  },
  ntr_auto: {
    name: 'Notice Trigger Registers (Automatic)',
    icon: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7A2,2 0 0,1 14,9C14,9.74 13.6,10.39 13,10.73V13H14V15H10V13H11V10.73C10.4,10.39 10,9.74 10,9A2,2 0 0,1 12,7Z',
    description: 'Drop in a policy before anything happens — RiskNinja builds the notice-trigger reference for the right line automatically.',
    route: '/ntr-auto'
  },
  ntr_first_party: {
    name: 'Notice Trigger Register — First-Party & Specialty',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Builds the notice-trigger reference for crime/fidelity, kidnap & ransom, environmental, or foreign/specialty programs — four artifacts, built before anything happens.',
    route: '/ntr-first-party'
  },
  ntr_property_liability: {
    name: 'Notice Trigger Register — Property & Liability',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Builds the notice-trigger reference for GL, property, auto, umbrella, or WC/EL programs — four artifacts, built before anything happens.',
    route: '/ntr-property-liability'
  },
  ntr_mgmt_professional: {
    name: 'Notice Trigger Register — Management & Professional',
    icon: 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
    description: 'Builds the notice-trigger reference for D&O, EPL, fiduciary, professional E&O/Tech E&O, or cyber programs — four artifacts, built before anything happens.',
    route: '/ntr-mgmt-professional'
  },
};

type WorkflowType = 'renewal' | 'document_comparison' | 'loss_run_analysis' | 'loss_run_analyzer' | 'loss_run_analyzer_v4' | 'loss_run_analyzer_v4_3' | 'year_over_year' | 'yoy_renewal' | 'personal_lines_yoy_renewal' | 'commercial_lines_exec_renewal' | 'client_renewal_presentation' | 'research_tool' | 'certificate_of_effort' | 'coverage_analysis' | 'insurance_requirements' | 'financial_analysis' | 'universal_analysis' | 'rate_analysis' | 'portfolio_analyzer' | 'homeowners_comparison' | 'personal_umbrella' | 'cyber_auto' | 'cyber_benchmark' | 'cyber_renewal_delta' | 'cyber_war_systemic' | 'cyber_ransomware_trap' | 'cyber_quote_comparison' | 'do_auto' | 'do_benchmark' | 'do_renewal_delta' | 'do_distress_scanner' | 'do_emerging_exposure' | 'home_auto' | 'home_owner_benchmark' | 'home_tenant_condo_benchmark' | 'home_quote_comparison' | 'home_renewal_delta' | 'home_dp_rental_benchmark' | 'home_dp_rental_renewal_delta' | 'home_hnw_benchmark' | 'plines_auto' | 'pa_benchmark' | 'pa_quote_comparison' | 'pa_renewal_delta' | 'umb_benchmark' | 'umb_quote_comparison' | 'umb_renewal_delta' | 'rt_auto' | 'rt_requirements_reviewer' | 'rt_conformance_alignment' | 'rt_compliance_verifier' | 'premdev_auto' | 'premdev_workers_comp' | 'premdev_prof_liability' | 'premdev_general_liability' | 'premdev_property' | 'premdev_package_bop' | 'lossrun_auto' | 'lossrun_fact_engine' | 'lossrun_mitigation_wc' | 're_auto' | 're_policy_extraction' | 're_risk_transfer' | 're_br_financials' | 're_zone_extractor' | 're_portfolio_brief' | 'cl_auto' | 'cl_gl_benchmark' | 'cl_property_benchmark' | 'cl_commercial_auto_benchmark' | 'cl_inland_marine_benchmark' | 'cl_excess_benchmark' | 'cl_package_benchmark' | 'cl_bop_benchmark' | 'cl_nytpao_flow' | 'cl_package_quote_comparison' | 'cl_bop_quote_comparison' | 'cl_xarch_quote_comparison' | 'cl_package_renewal_delta' | 'cl_bop_renewal_delta' | 'ca_auto' | 'ca_coverage_determination' | 'ca_portfolio_determination' | 'ca_carrier_position_audit' | 'ntr_auto' | 'ntr_first_party' | 'ntr_property_liability' | 'ntr_mgmt_professional';

interface WorkflowToolsProps {
  clientType: 'customer' | 'prospect' | 'suspect';
  clientId: string;
  clientName: string;
  contactName?: string;
  projectId?: string;
  projectName?: string;
  onToolClick?: (toolType: string) => void;
  onWorkflowComplete?: () => void;
  viewMode?: 'card' | 'list';
  /** `?workflow=<type>` deep link (E17 guide navigator) — opens this tool's
   * modal once on mount via the same catalog-launch path as a card click. */
  autoLaunchWorkflowType?: string;
  onAutoLaunchConsumed?: () => void;
}

/**
 * With ~31 BETA tools, the flat category needs internal structure. Groups
 * the BETA category's tools by suite (Cyber · D&O · Home Insurance ·
 * Premium Development · Loss Runs · Real Estate), auto tool first in each
 * group, in suite-registry order. Any tool that doesn't match a known suite
 * (shouldn't happen — defensive) falls back to its own trailing group.
 */
const groupBetaToolsBySuite = (tools: WorkflowTool[]): Array<{ id: string; displayName: string; tools: WorkflowTool[] }> => {
  const groups = SUITES.map((suite) => ({
    id: suite.id,
    displayName: suite.displayName,
    tools: tools
      .filter((t) => getSuiteForType(t.workflowType)?.id === suite.id)
      .sort((a, b) => (a.workflowType === suite.autoType ? -1 : b.workflowType === suite.autoType ? 1 : 0)),
  })).filter((g) => g.tools.length > 0);

  const grouped = new Set(groups.flatMap((g) => g.tools.map((t) => t.workflowType)));
  const leftover = tools.filter((t) => !grouped.has(t.workflowType));
  if (leftover.length > 0) groups.push({ id: 'other-beta', displayName: 'Other', tools: leftover });

  return groups;
};

const WorkflowTools: React.FC<WorkflowToolsProps> = ({ clientType, clientId, clientName, contactName, projectId, projectName, onToolClick, onWorkflowComplete, viewMode = 'card', autoLaunchWorkflowType, onAutoLaunchConsumed }) => {
  // Container class for tool/category grids: a single-column stack in list mode,
  // a responsive grid in card mode. Cards become full-width rows in list mode.
  const toolGridClass = viewMode === 'list'
    ? 'flex flex-col gap-2'
    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3';
  const { favorites, isFavorite, toggleFavorite, canAddMore, max: maxFavorites } = useWorkflowFavorites();
  const { recent, recordUsage } = useRecentWorkflows();
  const { user, token } = useAuth();
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType | null>(null);
  const { launchableType } = useWorkflowView();
  // Production view (from the API): one auto tool per suite + a collapsed Legacy section.
  const [catalogView, setCatalogView] = useState<'prod' | 'dev'>('dev');
  const [legacyOpen, setLegacyOpen] = useState(false);

  // Category state — the catalog renders as one flat, searchable page (no
  // category-cards -> drill-down navigation).
  const [categories, setCategories] = useState<WorkflowCategoryDetail[]>([]);
  const [uncategorizedTools, setUncategorizedTools] = useState<WorkflowTool[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch categories from the API
  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
      const url = `${apiUrl}/api/workflows/categories`;
      console.log('Fetching workflow categories from:', url);

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Categories received:', data);
        setCategories(data.categories || []);
        setUncategorizedTools(data.uncategorizedTools || []);
        setCatalogView(data.view === 'prod' ? 'prod' : 'dev');
      } else {
        console.error('Failed to fetch categories:', res.status);
        setCategoriesError('Failed to load categories');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategoriesError('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCategories();
    }
  }, [token, fetchCategories]);

  // Reload when the user switches Prod/Dev view (dev site only).
  useEffect(() => {
    const onChange = () => fetchCategories();
    window.addEventListener(WORKFLOW_VIEW_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(WORKFLOW_VIEW_CHANGED_EVENT, onChange);
  }, [fetchCategories]);

  // Filter tools by client type
  const filterToolsByClientType = (tools: WorkflowTool[]): WorkflowTool[] => {
    const allowedTools = TOOL_CONFIG[clientType];
    return tools.filter(tool => allowedTools.includes(tool.workflowType));
  };

  // Search matches a tool's display name or description (case-insensitive).
  const matchesSearch = (tool: WorkflowTool): boolean => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    const info = TOOL_INFO[tool.workflowType];
    const name = (tool.displayName || info?.name || '').toLowerCase();
    const description = (info?.description || '').toLowerCase();
    return name.includes(q) || description.includes(q);
  };

  const filterTools = (tools: WorkflowTool[]): WorkflowTool[] =>
    filterToolsByClientType(tools).filter(matchesSearch);

  // Get filtered categories with tools
  const getFilteredCategories = (): WorkflowCategoryDetail[] => {
    return categories
      .map(category => ({
        ...category,
        tools: filterTools(category.tools),
        toolCount: filterTools(category.tools).length,
      }))
      .filter(category => category.toolCount > 0);
  };

  // Get filtered uncategorized tools
  const getFilteredUncategorizedTools = (): WorkflowTool[] => {
    return filterTools(uncategorizedTools);
  };

  const handleToolClick = (requestedType: string) => {
    // Pinned/recent tools or deep links to a suite's individual tool open the
    // suite's auto tool in the production view.
    const toolType = launchableType(requestedType);
    recordUsage(toolType);
    if (onToolClick) {
      onToolClick(toolType);
      return;
    }

    const workflowTools = ['renewal', 'document_comparison', 'loss_run_analysis', 'loss_run_analyzer', 'loss_run_analyzer_v4', 'loss_run_analyzer_v4_3', 'year_over_year', 'yoy_renewal', 'personal_lines_yoy_renewal', 'commercial_lines_exec_renewal', 'client_renewal_presentation', 'research_tool', 'certificate_of_effort', 'coverage_analysis', 'insurance_requirements', 'financial_analysis', 'rate_analysis', 'portfolio_analyzer', 'homeowners_comparison', 'personal_umbrella', 'cyber_auto', 'cyber_benchmark', 'cyber_renewal_delta', 'cyber_war_systemic', 'cyber_ransomware_trap', 'cyber_quote_comparison', 'do_auto', 'do_benchmark', 'do_renewal_delta', 'do_distress_scanner', 'do_emerging_exposure', 'home_auto', 'home_owner_benchmark', 'home_tenant_condo_benchmark', 'home_quote_comparison', 'home_renewal_delta', 'home_dp_rental_benchmark', 'home_dp_rental_renewal_delta', 'home_hnw_benchmark', 'plines_auto', 'pa_benchmark', 'pa_quote_comparison', 'pa_renewal_delta', 'umb_benchmark', 'umb_quote_comparison', 'umb_renewal_delta', 'rt_auto', 'rt_requirements_reviewer', 'rt_conformance_alignment', 'rt_compliance_verifier', 'premdev_auto', 'premdev_workers_comp', 'premdev_prof_liability', 'premdev_general_liability', 'premdev_property', 'premdev_package_bop', 'lossrun_auto', 'lossrun_fact_engine', 'lossrun_mitigation_wc', 're_auto', 're_policy_extraction', 're_risk_transfer', 're_br_financials', 're_zone_extractor', 're_portfolio_brief', 'cl_auto', 'cl_gl_benchmark', 'cl_property_benchmark', 'cl_commercial_auto_benchmark', 'cl_inland_marine_benchmark', 'cl_excess_benchmark', 'cl_package_benchmark', 'cl_bop_benchmark', 'cl_nytpao_flow', 'cl_package_quote_comparison', 'cl_bop_quote_comparison', 'cl_xarch_quote_comparison', 'cl_package_renewal_delta', 'cl_bop_renewal_delta', 'ca_auto', 'ca_coverage_determination', 'ca_portfolio_determination', 'ca_carrier_position_audit', 'ntr_auto', 'ntr_first_party', 'ntr_property_liability', 'ntr_mgmt_professional'];

    if (workflowTools.includes(toolType)) {
      setSelectedWorkflow(toolType as WorkflowType);
      setShowWorkflowModal(true);
    }
  };

  const handleCloseWorkflowModal = () => {
    setShowWorkflowModal(false);
    setSelectedWorkflow(null);

    if (onWorkflowComplete) {
      onWorkflowComplete();
    }
  };

  // E17 guide navigator: `?workflow=<type>` deep link opens this tool once,
  // via the same path a catalog card click would take.
  useEffect(() => {
    if (!autoLaunchWorkflowType) return;
    handleToolClick(autoLaunchWorkflowType);
    onAutoLaunchConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLaunchWorkflowType]);

  const handleWorkflowSwitch = (newWorkflowType: string) => {
    setSelectedWorkflow(newWorkflowType as WorkflowType);
  };

  // Render a tool card. `categoryName` (when known) tints the icon square so
  // cards read as belonging to a section at a glance, instead of a uniform
  // wall of primary-colored squares.
  const renderToolCard = (tool: WorkflowTool, categoryName?: string) => {
    const toolInfo = TOOL_INFO[tool.workflowType];
    if (!toolInfo) return null;
    const pinned = isFavorite(tool.workflowType);
    const tint = categoryName ? pickIcon(categoryName).tint : 'text-primary bg-primary/10';

    return (
      <div
        key={tool.workflowType}
        onClick={() => handleToolClick(tool.workflowType)}
        className={`relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all cursor-pointer group hover:border-primary ${viewMode === 'list' ? 'px-4 py-2.5' : 'p-4'}`}
      >
        {/* Pin / star toggle in the corner */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!pinned && !canAddMore) return;
            toggleFavorite(tool.workflowType);
          }}
          aria-label={pinned ? 'Unpin from Quick Start' : 'Pin to Quick Start'}
          aria-pressed={pinned}
          title={pinned ? 'Unpin from Quick Start' : (canAddMore ? 'Pin to Quick Start' : `Pin limit reached (${maxFavorites})`)}
          className={`absolute top-2 right-2 p-1 rounded transition-colors ${
            pinned
              ? 'text-amber-500 hover:text-amber-600'
              : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 opacity-0 group-hover:opacity-100'
          } ${!pinned && !canAddMore ? 'cursor-not-allowed' : ''}`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>

        <div className={`flex gap-3 ${viewMode === 'list' ? 'items-center' : 'items-start'}`}>
          <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${tint}`}>
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d={toolInfo.icon} />
            </svg>
          </div>
          <div className={`flex-1 min-w-0 ${viewMode === 'list' ? 'flex items-center gap-3' : ''}`}>
            <h3 className={`text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors pr-5 ${viewMode === 'list' ? 'flex-shrink-0' : ''}`}>
              {tool.displayName || toolInfo.name}
            </h3>
            <p className={`text-xs text-gray-600 dark:text-gray-400 ${viewMode === 'list' ? 'line-clamp-1 mt-0' : 'line-clamp-2 mt-0.5'}`}>
              {toolInfo.description}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Flat lookup of every visible tool by workflowType so the Quick Start row
  // can resolve favorites that live in any category.
  const flatToolByType = useMemo(() => {
    const map = new Map<string, WorkflowTool>();
    for (const cat of categories) {
      for (const t of filterToolsByClientType(cat.tools)) {
        if (!map.has(t.workflowType)) map.set(t.workflowType, t);
      }
    }
    for (const t of filterToolsByClientType(uncategorizedTools)) {
      if (!map.has(t.workflowType)) map.set(t.workflowType, t);
    }
    return map;
  }, [categories, uncategorizedTools, clientType]);

  const favoriteTools = useMemo(
    () => favorites.map((wf) => flatToolByType.get(wf)).filter((t): t is WorkflowTool => !!t),
    [favorites, flatToolByType]
  );

  // E13 — "Recently used" row: excludes anything already pinned in Quick
  // Start (no double rows), capped at 4.
  const recentTools = useMemo(
    () =>
      recent
        .filter((r) => !favorites.includes(r.workflowType))
        .map((r) => flatToolByType.get(r.workflowType))
        .filter((t): t is WorkflowTool => !!t)
        .slice(0, 4),
    [recent, favorites, flatToolByType]
  );

  // Loading state
  if (categoriesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Error state or no categories - fall back to flat view
  const filteredCategories = getFilteredCategories();
  const filteredUncategorized = getFilteredUncategorizedTools();
  // Production view: classic categories are folded into one collapsed Legacy section.
  const isLegacyCategory = (c: WorkflowCategoryDetail) => catalogView === 'prod' && !!(c as any).legacy;
  const mainCategories = filteredCategories.filter((c) => !isLegacyCategory(c));
  const legacyCategories = filteredCategories.filter(isLegacyCategory);
  const legacyExpanded = legacyOpen || !!searchQuery.trim();

  if (categoriesError || (filteredCategories.length === 0 && filteredUncategorized.length === 0)) {
    // Fallback to flat view using TOOL_CONFIG
    const baseTools = TOOL_CONFIG[clientType];
    return (
      <>
        <div className={toolGridClass}>
          {baseTools.map(toolType => {
            const toolInfo = TOOL_INFO[toolType];
            if (!toolInfo) return null;

            return (
              <div
                key={toolType}
                onClick={() => handleToolClick(toolType)}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group hover:border-primary"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="currentColor">
                      <path d={toolInfo.icon} />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                      {toolInfo.name}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                      {toolInfo.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showWorkflowModal && selectedWorkflow && (
          <WorkflowModal
            isOpen={showWorkflowModal}
            onClose={handleCloseWorkflowModal}
            workflowType={selectedWorkflow}
            clientId={clientId}
            clientName={clientName}
            clientType={clientType}
            contactName={contactName}
            projectId={projectId}
            projectName={projectName}
            onWorkflowSwitch={handleWorkflowSwitch}
          />
        )}
      </>
    );
  }

  // Flat, searchable catalog — no category-cards -> drill-down. Quick Start
  // stays pinned above everything; a search box + quick-jump chips sit above
  // the sections themselves.
  return (
    <>
      <div>
        {/* Quick Start: user-pinned workflow tools */}
        {favoriteTools.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Quick Start</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Your pinned tools · star any tool to add up to {maxFavorites}
              </span>
            </div>
            <div className={toolGridClass}>
              {favoriteTools.map((tool) => renderToolCard(tool))}
            </div>
          </div>
        )}

        {/* Recently used — per-device recency, excludes anything already pinned above */}
        {recentTools.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recently used</h3>
            </div>
            <div className={toolGridClass}>
              {recentTools.map((tool) => renderToolCard(tool))}
            </div>
          </div>
        )}

        {/* Search + category quick-jump */}
        <div className="mb-6">
          <div className="relative mb-3">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search workflows by name or description…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          {catalogView === 'dev' && filteredCategories.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {filteredCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => document.getElementById(`wf-category-${category.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sections */}
        {filteredCategories.length === 0 && filteredUncategorized.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
            {searchQuery.trim() ? `No workflows match "${searchQuery.trim()}".` : 'No workflows available.'}
          </div>
        ) : (
          <div className="space-y-8">
            {mainCategories.map((category) => {
              // In the production view the BETA category holds just the suite
              // auto tools, shown as plain "Tools" — no Beta badge, no grouping.
              const isProdTools = catalogView === 'prod' && category.name === 'BETA';
              const isBeta = category.name === 'BETA' && !isProdTools;
              const icon = pickIcon(category.name);
              return (
                <div
                  key={category.id}
                  id={`wf-category-${category.id}`}
                  className={isBeta ? 'pt-1 border-t-2 border-violet-300 dark:border-violet-700' : undefined}
                >
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                    {isBeta ? (
                      <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase rounded-full bg-gradient-to-r from-amber-400 to-violet-500 text-white flex-shrink-0">
                        Beta
                      </span>
                    ) : (
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${icon.tint}`}>
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                          <path d={icon.path} />
                        </svg>
                      </div>
                    )}
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {isBeta ? 'New-Generation Tool Suites' : isProdTools ? 'Tools' : category.name}
                    </h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {category.toolCount} {category.toolCount === 1 ? 'tool' : 'tools'}
                    </span>
                  </div>
                  {isBeta ? (
                    <div className="space-y-6">
                      {groupBetaToolsBySuite(category.tools).map((group) => (
                        <div key={group.id}>
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            {group.displayName}
                          </h4>
                          <div className={toolGridClass}>
                            {group.tools.map((tool) => renderToolCard(tool, category.name))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={toolGridClass}>
                      {category.tools.map((tool) => renderToolCard(tool, category.name))}
                    </div>
                  )}
                </div>
              );
            })}

            {legacyCategories.length > 0 && (
              <div id="wf-category-legacy">
                <button
                  type="button"
                  onClick={() => setLegacyOpen((o) => !o)}
                  aria-expanded={legacyExpanded}
                  className="w-full flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 text-left"
                >
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${legacyExpanded ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Legacy</h3>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {legacyCategories.reduce((n, c) => n + c.toolCount, 0)} tools · the earlier analysis tools
                  </span>
                </button>
                {legacyExpanded && (
                  <div className="space-y-6">
                    {legacyCategories.map((category) => (
                      <div key={category.id}>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{category.name}</h4>
                        <div className={toolGridClass}>
                          {category.tools.map((tool) => renderToolCard(tool, category.name))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {filteredUncategorized.length > 0 && (
              <div id="wf-category-other">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Other Tools</h3>
                </div>
                <div className={toolGridClass}>
                  {filteredUncategorized.map(tool => renderToolCard(tool))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Workflow Modal */}
      {showWorkflowModal && selectedWorkflow && (
        <WorkflowModal
          isOpen={showWorkflowModal}
          onClose={handleCloseWorkflowModal}
          workflowType={selectedWorkflow}
          clientId={clientId}
          clientName={clientName}
          clientType={clientType}
          contactName={contactName}
          projectId={projectId}
          projectName={projectName}
          onWorkflowSwitch={handleWorkflowSwitch}
        />
      )}
    </>
  );
};

export default WorkflowTools;
